import { SUPPORT_EMAIL, SUPPORT_PHONE_NUMBER } from "../constants";
import { getCurrentMainAssetPath } from "./buildIdentifier";
import {
  attemptFirestoreClientRecovery,
  attemptChunkLoadRecovery,
  isChunkLoadFailureMessage,
} from "./buildRefresh";
import { redactSensitiveRoutePathname } from "./routePath";

const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");
const LOCAL_API_BASE_URL =
  process.env.REACT_APP_LOCAL_API_BASE_URL || "http://localhost:8088";
const DEFAULT_REPORT_URL = isLocalhost
  ? `${LOCAL_API_BASE_URL}/api/crash/report`
  : "/client-errors/report";
const DEFAULT_SUBSCRIBE_URL = isLocalhost
  ? `${LOCAL_API_BASE_URL}/api/crash/subscribe`
  : "/client-errors/subscribe";
const DEFAULT_BOOT_TIMEOUT_MS = 15_000;
const DEFAULT_BOOT_TIMEOUT_RECOVERY_DELAY_MS = 750;
const BOOT_TIMEOUT_RECOVERY_STORAGE_KEY = "ll:boot-timeout-recovery:last-attempt";
const DEFAULT_DEDUPE_WINDOW_MS = 30_000;
const DEFAULT_MAX_PENDING_REPORTS = 10;
const MAX_BREADCRUMBS = 25;
const EMERGENCY_OVERLAY_ID = "ll-client-crash-overlay";
const WHITE_SCREEN_KINDS = new Set([
  "boot_timeout",
  "react_render_crash",
  "chunk_load_failure",
  "firestore_client_failure",
]);
const FIRST_PARTY_APP_HOSTNAMES = new Set([
  "ourlinkedlives.com",
  "www.ourlinkedlives.com",
  "ourlinkedlives.firebaseapp.com",
  "ourlinkedlives.web.app",
  "app.linkedlives.com",
]);
const ALLOWED_KINDS = new Set([
  "boot_timeout",
  "react_render_crash",
  "uncaught_runtime_error",
  "unhandled_promise_rejection",
  "chunk_load_failure",
  "firestore_client_failure",
]);
const WEBKIT_MESSAGE_HANDLER_BRIDGE_RE =
  /\bwindow\.webkit\.messageHandlers\b(?:[\s\S]{0,140}postMessage)?/iu;
const CONTACT_AUTOFILL_BRIDGE_RE =
  /\bsetContactAutofillValuesFromBridge\b/iu;
const CHROMIUM_IOS_GCRWEB_BRIDGE_RE =
  /\b__gCrWeb\b/iu;
const ANDROID_JAVA_BRIDGE_OBJECT_GONE_RE =
  /\bError invoking (?:postMessage|enableDidUserTypeOnKeyboardLogging|enableButtonsClickedMetaDataLogging): Java object is gone\b/iu;
const HTML_DOCUMENT_SCRIPT_SYNTAX_ERROR_RE =
  /\bUnexpected token\s+['"]?<['"]?/iu;
const BENIGN_BROWSER_STORAGE_ERROR_RE =
  /\b(?:Connection to Indexed Database server lost|Database deleted by request of the user|internal error was encountered in the Indexed Database server)\b/iu;
const BENIGN_BROWSER_EXTENSION_ERROR_RE =
  /\b(?:Invalid call to runtime\.sendMessage\(\)\. Tab not found|(?:Can't find variable:\s*|undefined is not an object \(evaluating 'window\.)__firefox__|_?AutofillCallbackHandler|WKWebView API client did not respond to this postMessage|SCDynimacBridge|DarkReader)\b/iu;
const CONTENT_SCRIPT_INJECTION_ERROR_RE =
  /\b(?:contentScriptData\.init_ts|undefined is not an object \(evaluating ['"]contentScriptData\.init_ts['"]\))\b/iu;
const BROWSER_WALLET_EXTENSION_ERROR_RE =
  /\bwindow\.ethereum\.selectedAddress\b/iu;
const THIRD_PARTY_IFRAME_POST_MESSAGE_RE =
  /\bcontentWindow\.postMessage\b|\bsendMessageToIFrames\b/iu;
const RESIZE_OBSERVER_LOOP_RE =
  /\bResizeObserver loop (?:completed with undelivered notifications|limit exceeded)\b/iu;
const GOOGLE_MAPS_LEGACY_DIALOG_RE =
  /\bHTMLDialogElement\b/iu;
const GOOGLE_MAPS_MODULE_LOAD_ERROR_RE =
  /\bCould not load\s+["'][^"']+["']\.?/iu;
const GOOGLE_MAPS_SCRIPT_FRAME_RE =
  /(?:maps\.googleapis\.com\/maps\/api\/js|@\/maps\/api\/js)/iu;
const GOOGLE_MAPS_AUTH_FAILURE_MESSAGE =
  "Google Maps JavaScript API authentication failed.";
const FIRESTORE_ASYNC_QUEUE_FAILURE_RE =
  /\bFIRESTORE\s*\([^)]+\)\s+INTERNAL ASSERTION FAILED:\s*(?:AsyncQueue is already failed|Unexpected state)\s*\(ID:\s*(?:b815|ca9)\)/iu;
const NO_DETAIL_UNHANDLED_REJECTION_RE =
  /^(?:Error\s+)?(?:Unknown error|Non-Error crash object)$/iu;

const isPlainObject = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const LIKELY_PHONE_NUMBER_RE =
  /(^|[^A-Za-z0-9])((?:\+?1[\s.-]*)?(?:\(\d{3}\)|\d{3})[\s.-]*\d{3}[\s.-]*\d{4})\b/gu;

const shouldPreserveNumericAssetReference = ({
  source = "",
  candidateStart = 0,
  candidateEnd = 0,
} = {}) => {
  const before = source
    .slice(Math.max(0, candidateStart - 40), candidateStart)
    .toLowerCase();
  const after = source.slice(candidateEnd, candidateEnd + 40).toLowerCase();

  return (
    /(?:^|[/:])static\/(?:js|css|media)\/[a-z0-9._-]*$/u.test(before) ||
    /\bchunk\s*$/u.test(before) ||
    /^\.(?:chunk\.)?(?:js|mjs|css|map)\b/u.test(after)
  );
};

const truncateString = (value, maxLength = 120) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) {
    return null;
  }
  return normalized.slice(0, maxLength);
};

const redactLikelyPhoneNumbers = (value = "") =>
  String(value || "").replace(
    LIKELY_PHONE_NUMBER_RE,
    (match, prefix, candidate, offset, source) => {
      const candidateStart = offset + prefix.length;
      const candidateEnd = candidateStart + candidate.length;

      if (
        shouldPreserveNumericAssetReference({
          source,
          candidateStart,
          candidateEnd,
        })
      ) {
        return match;
      }

      return `${prefix}[redacted-phone]`;
    },
  );

const redactSensitiveText = (value = "") =>
  redactLikelyPhoneNumbers(
    String(value || "")
    .replace(
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu,
      "[redacted-email]",
    )
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/gu, "[redacted-token]")
    .replace(
      /((?:token|auth|authorization|secret|session|api[_-]?key|key|email|phone|mobile))=[^&\s]+/giu,
      "$1=[redacted]",
    ),
  );

const sanitizeFreeText = (value, maxLength = 120) =>
  truncateString(redactSensitiveText(value), maxLength);

const escapeHtml = (value = "") =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const formatSupportPhone = (value = SUPPORT_PHONE_NUMBER) => {
  const digits = String(value || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) {
    return String(value || "");
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const normalizeSubscriptionEmail = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized || normalized.length > 200) {
    return null;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(normalized) ? normalized : null;
};

const normalizeSubscriptionPhone = (value = "") => {
  const digits = String(value || "").replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (normalized.length !== 10) {
    return null;
  }

  return `+1${normalized}`;
};

const isLocalHostname = (hostname = "") =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "[::1]" ||
  hostname.endsWith(".local");

const sanitizePath = (value, origin = "http://localhost") => {
  const normalized = truncateString(value, 500);
  if (!normalized) {
    return null;
  }

  try {
    const url = new URL(normalized, origin);
    return truncateString(redactSensitiveRoutePathname(url.pathname), 200);
  } catch (error) {
    const pathname = normalized.split(/[?#]/u, 1)[0];
    return pathname.startsWith("/")
      ? truncateString(redactSensitiveRoutePathname(pathname), 200)
      : null;
  }
};

export const getReferrerPath = (
  doc = typeof document !== "undefined" ? document : null,
  locationRef =
    typeof window !== "undefined" ? window.location : { origin: "http://localhost" },
) => {
  const referrer = truncateString(doc?.referrer || "", 500);
  if (!referrer) {
    return null;
  }

  try {
    const url = new URL(referrer);
    if (url.origin !== locationRef.origin) {
      return null;
    }
    return truncateString(redactSensitiveRoutePathname(url.pathname), 200);
  } catch (error) {
    return null;
  }
};

const sanitizePayloadObject = (value, maxKeys = 8, maxStringLength = 120) => {
  if (!isPlainObject(value)) {
    return null;
  }

  const entries = Object.entries(value)
    .slice(0, maxKeys)
    .flatMap(([key, rawValue]) => {
      const normalizedKey = truncateString(key, 40);
      if (!normalizedKey) {
        return [];
      }

      if (
        rawValue == null ||
        typeof rawValue === "boolean" ||
        typeof rawValue === "number"
      ) {
        return [[normalizedKey, rawValue]];
      }

      if (typeof rawValue === "string") {
        return [[normalizedKey, sanitizeFreeText(rawValue, maxStringLength)]];
      }

      if (Array.isArray(rawValue)) {
        const normalizedArray = rawValue
          .slice(0, 4)
          .map((item) => {
            if (
              item == null ||
              typeof item === "boolean" ||
              typeof item === "number"
            ) {
              return item;
            }
            if (typeof item === "string") {
              return sanitizeFreeText(item, maxStringLength);
            }
            return null;
          })
          .filter((item) => item !== null);

        return normalizedArray.length ? [[normalizedKey, normalizedArray]] : [];
      }

      return [];
    });

  return entries.length ? Object.fromEntries(entries) : null;
};

export const sanitizeBreadcrumbData = (value) =>
  sanitizePayloadObject(value, 6, 80);

const sanitizeBreadcrumb = (value = {}) => {
  const breadcrumb = {
    at: truncateString(value.at, 48),
    category: truncateString(value.category, 40) || "app",
    message: sanitizeFreeText(value.message, 120),
    data: sanitizeBreadcrumbData(value.data),
  };

  if (!breadcrumb.message && !breadcrumb.data) {
    return null;
  }

  return breadcrumb;
};

const sanitizeUserContext = (value = {}) => {
  if (!isPlainObject(value)) {
    return null;
  }

  return {
    uid: truncateString(value.uid, 128),
    role: truncateString(value.role, 32),
    isAuthenticated: Boolean(value.isAuthenticated),
    claimsSummary: sanitizePayloadObject(value.claimsSummary, 6, 80),
  };
};

const sanitizeSessionContext = (value = {}) => {
  if (!isPlainObject(value)) {
    return null;
  }

  return {
    sessionId: truncateString(value.sessionId, 128),
    experimentGroup: truncateString(value.experimentGroup, 64),
    systemSummary: sanitizePayloadObject(value.systemSummary, 10, 80),
  };
};

const readJsonStorage = (storage, key) => {
  try {
    const value = storage?.getItem?.(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const writeJsonStorage = (storage, key, value) => {
  try {
    storage?.setItem?.(key, JSON.stringify(value));
    return true;
  } catch (error) {
    return false;
  }
};

const getWindowStorage = (windowRef, storageName) => {
  try {
    return windowRef?.[storageName] || null;
  } catch (error) {
    return null;
  }
};

const normalizeMessage = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/https?:\/\/[^/)\s]+/giu, "")
    .replace(/\bchunk\s+\d+\b/giu, "chunk #")
    .replace(/\/static\/js\/\d+\.[^/\s)]+\.chunk\.js/giu, "/static/js/chunk.js")
    .replace(/\/static\/js\/([^./)\s]+)\.[^/\s)]+\.js/giu, "/static/js/$1.js")
    .replace(/\b[0-9a-f]{8,}\b/giu, "#")
    .replace(/\s+/gu, " ")
    .slice(0, 200);

const normalizeStackFrame = (value) =>
  String(value || "")
    .trim()
    .replace(/^at\s+/u, "")
    .replace(/https?:\/\/[^/)\s]+/giu, "")
    .replace(/main\.[a-z0-9]{6,}\.js/giu, "main.js")
    .replace(/\/static\/js\/(\d+)\.[a-z0-9]{6,}\.chunk\.js/giu, "/static/js/chunk.js")
    .replace(/\/static\/js\/([^./)\s]+)\.[a-z0-9]{6,}\.js/giu, "/static/js/$1.js")
    .replace(/\s+/gu, " ")
    .slice(0, 240);

const getStackFrames = (stack) =>
  String(stack || "")
    .split("\n")
    .map((line) => normalizeStackFrame(line))
    .filter(Boolean);

const getResourceUrl = (value = "", locationRef = null) => {
  const normalized = truncateString(value, 500);
  if (!normalized) {
    return null;
  }

  try {
    return new URL(normalized, locationRef?.origin || "http://localhost");
  } catch (error) {
    return null;
  }
};

const isFirstPartyResourceHost = (url, locationRef = null) => {
  const resourceHostname = String(url?.hostname || "").toLowerCase();
  const currentHostname = String(locationRef?.hostname || "").toLowerCase();

  if (!resourceHostname || !currentHostname) {
    return false;
  }

  if (resourceHostname === currentHostname) {
    return true;
  }

  return (
    FIRST_PARTY_APP_HOSTNAMES.has(resourceHostname) &&
    FIRST_PARTY_APP_HOSTNAMES.has(currentHostname)
  );
};

const isAppJavaScriptResource = ({
  resourceType = "",
  resourceUrl = "",
  locationRef = null,
} = {}) => {
  if (String(resourceType || "").toLowerCase() !== "script") {
    return false;
  }

  const url = getResourceUrl(resourceUrl, locationRef);
  if (!url || !isFirstPartyResourceHost(url, locationRef)) {
    return false;
  }

  return /^\/static\/js\/.+\.(js|mjs)$/iu.test(url.pathname);
};

const hasCompiledAppStackFrame = (stack = "") =>
  getStackFrames(stack).some((frame) => frame.includes("/static/js/"));

const isLikelyThirdPartyBrowserBridgeError = ({
  message = "",
  stack = "",
} = {}) =>
  (
    WEBKIT_MESSAGE_HANDLER_BRIDGE_RE.test(`${message}\n${stack}`) ||
    CONTACT_AUTOFILL_BRIDGE_RE.test(`${message}\n${stack}`) ||
    CHROMIUM_IOS_GCRWEB_BRIDGE_RE.test(`${message}\n${stack}`) ||
    ANDROID_JAVA_BRIDGE_OBJECT_GONE_RE.test(`${message}\n${stack}`)
  ) &&
  !hasCompiledAppStackFrame(stack);

const getComponentFrames = (componentStack) =>
  String(componentStack || "")
    .split("\n")
    .map((line) => normalizeStackFrame(line))
    .filter(Boolean);

const extractTopAppFrame = (stack) => {
  const frames = getStackFrames(stack);
  if (!frames.length) {
    return null;
  }

  return (
    frames.find((frame) => frame.includes("/static/js/")) ||
    frames.find((frame) => frame.includes("main.js")) ||
    frames[0]
  );
};

const extractTopComponentFrame = (componentStack) => {
  const frames = getComponentFrames(componentStack);
  return frames[0] || null;
};

const isChunkLoadMessage = ({ message = "", name = "", stack = "" } = {}) => {
  return isChunkLoadFailureMessage({ message, name, stack });
};

const isFirestoreAsyncQueueFailureMessage = ({
  message = "",
  name = "",
  stack = "",
} = {}) => FIRESTORE_ASYNC_QUEUE_FAILURE_RE.test(`${name} ${message} ${stack}`);

const isLikelyBenignBrowserNoise = ({
  details = {},
  source = "",
  hasReactMounted = false,
} = {}) => {
  const haystack = `${details?.name || ""} ${details?.message || ""} ${
    details?.stack || ""
  }`;

  if (RESIZE_OBSERVER_LOOP_RE.test(haystack)) {
    return "ignored_resize_observer_loop";
  }

  if (BENIGN_BROWSER_EXTENSION_ERROR_RE.test(haystack)) {
    return "ignored_browser_extension_error";
  }

  if (
    CONTENT_SCRIPT_INJECTION_ERROR_RE.test(haystack) &&
    !hasCompiledAppStackFrame(details?.stack)
  ) {
    return "ignored_browser_extension_error";
  }

  if (
    BROWSER_WALLET_EXTENSION_ERROR_RE.test(haystack) &&
    !hasCompiledAppStackFrame(details?.stack)
  ) {
    return "ignored_browser_extension_error";
  }

  if (
    source === "window_error" &&
    THIRD_PARTY_IFRAME_POST_MESSAGE_RE.test(haystack) &&
    !hasCompiledAppStackFrame(details?.stack)
  ) {
    return "ignored_third_party_iframe_bridge_error";
  }

  if (
    source === "unhandledrejection" &&
    hasReactMounted &&
    BENIGN_BROWSER_STORAGE_ERROR_RE.test(haystack)
  ) {
    return "ignored_browser_storage_transient";
  }

  if (
    source === "unhandledrejection" &&
    GOOGLE_MAPS_LEGACY_DIALOG_RE.test(haystack) &&
    String(details?.stack || "").includes("maps.googleapis.com/maps/api/js")
  ) {
    return "ignored_google_maps_legacy_dialog_error";
  }

  if (
    source === "unhandledrejection" &&
    GOOGLE_MAPS_MODULE_LOAD_ERROR_RE.test(haystack) &&
    GOOGLE_MAPS_SCRIPT_FRAME_RE.test(haystack) &&
    !hasCompiledAppStackFrame(details?.stack)
  ) {
    return "ignored_google_maps_module_load_error";
  }

  if (
    source === "unhandledrejection" &&
    hasReactMounted &&
    !truncateString(details?.stack, 6_000) &&
    NO_DETAIL_UNHANDLED_REJECTION_RE.test(haystack.trim())
  ) {
    return "ignored_no_detail_unhandled_rejection";
  }

  return null;
};

const isLikelyHtmlDocumentScriptSyntaxError = ({
  message = "",
  name = "",
  stack = "",
  componentStack = "",
  source = "",
  filename = "",
  locationRef = null,
  hasReactMounted = null,
} = {}) => {
  const normalizedName = String(name || "").trim().toLowerCase();
  if (
    normalizedName &&
    normalizedName !== "syntaxerror" &&
    normalizedName !== "error"
  ) {
    return false;
  }

  if (source !== "window_error") {
    return false;
  }

  if (!HTML_DOCUMENT_SCRIPT_SYNTAX_ERROR_RE.test(String(message || ""))) {
    return false;
  }

  if (truncateString(stack, 6_000) || truncateString(componentStack, 3_000)) {
    return false;
  }

  const scriptFilename = truncateString(filename, 500);
  if (!scriptFilename) {
    return hasReactMounted === true;
  }

  return isAppJavaScriptResource({
    resourceType: "script",
    resourceUrl: scriptFilename,
    locationRef,
  });
};

export const classifyClientCrashKind = ({
  kind = "",
  message = "",
  name = "",
  stack = "",
  componentStack = "",
  source = "",
  filename = "",
  locationRef = null,
  hasReactMounted = null,
} = {}) => {
  const normalizedKind = String(kind || "").trim();

  if (isChunkLoadMessage({ message, name, stack })) {
    return "chunk_load_failure";
  }

  if (isFirestoreAsyncQueueFailureMessage({ message, name, stack })) {
    return "firestore_client_failure";
  }

  if (
    isLikelyHtmlDocumentScriptSyntaxError({
      message,
      name,
      stack,
      componentStack,
      source,
      filename,
      locationRef,
      hasReactMounted,
    })
  ) {
    return "chunk_load_failure";
  }

  if (ALLOWED_KINDS.has(normalizedKind)) {
    return normalizedKind;
  }

  if (source === "react_boundary") {
    return "react_render_crash";
  }

  if (source === "boot_watchdog") {
    return "boot_timeout";
  }

  if (source === "unhandledrejection") {
    return "unhandled_promise_rejection";
  }

  return "uncaught_runtime_error";
};

export const buildClientCrashFingerprint = ({
  kind,
  message,
  stack,
  componentStack,
}) => {
  const classification = classifyClientCrashKind({
    kind,
    message,
    stack,
    componentStack,
  });

  if (classification === "chunk_load_failure") {
    return "chunk_load_failure|chunk_load_failure||";
  }

  if (classification === "firestore_client_failure") {
    return "firestore_client_failure|async_queue_failed||";
  }

  return [
    classification,
    normalizeMessage(message),
    extractTopAppFrame(stack) || "",
    extractTopComponentFrame(componentStack) || "",
  ].join("|");
};

const buildFallbackDeviceSummary = ({
  navigatorRef = typeof navigator !== "undefined" ? navigator : null,
  windowRef = typeof window !== "undefined" ? window : null,
} = {}) => {
  const userAgent = truncateString(navigatorRef?.userAgent || "", 180);
  const browserMatch = userAgent?.match(
    /(firefox|msie|trident|chrome|safari|edge|opera|opr|edg)\/?\s*(\d+)/iu,
  );

  return sanitizePayloadObject({
    browser: browserMatch
      ? `${browserMatch[1]} ${browserMatch[2] || "unknown"}`
      : "unknown",
    language: truncateString(navigatorRef?.language || "", 24),
    screenSize:
      windowRef?.screen?.width && windowRef?.screen?.height
        ? `${windowRef.screen.width}x${windowRef.screen.height}`
        : null,
    timeZone: truncateString(
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      48,
    ),
    userAgent,
  });
};

const getByteLength = (value) => {
  let serialized = "";
  try {
    serialized = JSON.stringify(value);
  } catch (error) {
    serialized = JSON.stringify({
      kind: value?.kind || "uncaught_runtime_error",
      message: "Crash payload could not be serialized",
    });
  }
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(serialized).length;
  }
  return serialized.length;
};

const fitPayloadToBudget = (payload, maxBytes = 30_000) => {
  const nextPayload = { ...payload };

  if (getByteLength(nextPayload) <= maxBytes) {
    return nextPayload;
  }

  if (Array.isArray(nextPayload.breadcrumbs) && nextPayload.breadcrumbs.length > 10) {
    nextPayload.breadcrumbs = nextPayload.breadcrumbs.slice(-10);
  }

  if (getByteLength(nextPayload) <= maxBytes) {
    return nextPayload;
  }

  nextPayload.stack = truncateString(nextPayload.stack, 3_000);
  nextPayload.componentStack = truncateString(nextPayload.componentStack, 1_500);
  nextPayload.message = truncateString(nextPayload.message, 160);

  return nextPayload;
};

const getErrorDetails = (error) => {
  if (error instanceof Error) {
    return {
      name: truncateString(error.name || "Error", 80),
      message: sanitizeFreeText(error.message || "Unknown error", 240),
      stack: sanitizeFreeText(error.stack || "", 6_000),
    };
  }

  if (typeof error === "string") {
    return {
      name: "Error",
      message: sanitizeFreeText(error, 240),
      stack: null,
    };
  }

  if (isPlainObject(error)) {
    return {
      name: truncateString(error.name || "Error", 80),
      message:
        sanitizeFreeText(error.message, 240) ||
        sanitizeFreeText(error.code, 120) ||
        "Non-Error crash object",
      stack: sanitizeFreeText(error.stack || "", 6_000),
    };
  }

  return {
    name: "Error",
    message: sanitizeFreeText(String(error || "Unknown error"), 240),
    stack: null,
  };
};

const isOpaqueCrossOriginScriptError = (
  event = {},
  details = {},
  locationRef = null,
) => {
  const normalizedMessage = normalizeMessage(details?.message || event?.message);
  const hasScriptErrorMessage =
    normalizedMessage === "script error." || normalizedMessage === "script error";

  if (!hasScriptErrorMessage || event?.error || details?.stack) {
    return false;
  }

  const filename = truncateString(event?.filename, 500);
  const lineNumber = Number(event?.lineno || 0);
  const columnNumber = Number(event?.colno || 0);

  if (filename) {
    return !isAppJavaScriptResource({
      resourceType: "script",
      resourceUrl: filename,
      locationRef,
    });
  }

  return !lineNumber && !columnNumber;
};

export const createClientCrashReporter = (dependencies = {}) => {
  const windowRef =
    dependencies.windowRef || (typeof window !== "undefined" ? window : null);
  const documentRef =
    dependencies.documentRef || (typeof document !== "undefined" ? document : null);
  const navigatorRef =
    dependencies.navigatorRef ||
    (typeof navigator !== "undefined" ? navigator : null);
  const performanceRef =
    dependencies.performanceRef ||
    windowRef?.performance ||
    (typeof performance !== "undefined" ? performance : null);
  const fetchImpl =
    dependencies.fetchImpl ||
    (typeof fetch === "function" ? fetch.bind(windowRef) : null);
  const dateNow = dependencies.dateNow || (() => Date.now());
  const sessionStorageRef =
    dependencies.sessionStorageRef || getWindowStorage(windowRef, "sessionStorage");

  const state = {
    initialized: false,
    initAt: 0,
    initMonotonicAt: null,
    hasReactMounted: false,
    preMountCrashCaptured: false,
    breadcrumbs: [],
    recentFingerprints: new Map(),
    pendingReports: [],
    bootTimerId: null,
    bootRecovery: null,
    firestoreClientRecoveryPending: false,
    googleMapsAuthFailureCaptured: false,
    priorGoogleMapsAuthFailureHandler: null,
    overlayVisible: false,
    userContext: null,
    sessionContext: null,
    config: {
      enabled: true,
      reportUrl: DEFAULT_REPORT_URL,
      subscribeUrl: DEFAULT_SUBSCRIBE_URL,
      bootTimeoutMs: DEFAULT_BOOT_TIMEOUT_MS,
      dedupeWindowMs: DEFAULT_DEDUPE_WINDOW_MS,
      maxPendingReports: DEFAULT_MAX_PENDING_REPORTS,
      supportEmail: SUPPORT_EMAIL,
      supportPhone: SUPPORT_PHONE_NUMBER,
      isProduction:
        process.env.NODE_ENV === "production" &&
        !isLocalHostname(windowRef?.location?.hostname || ""),
    },
  };

  const pruneRecentFingerprints = () => {
    const now = dateNow();
    for (const [key, value] of state.recentFingerprints.entries()) {
      if (now - value > state.config.dedupeWindowMs) {
        state.recentFingerprints.delete(key);
      }
    }
  };

  const shouldUseProductionBehavior = () => Boolean(state.config.isProduction);

  const monotonicNow = () => {
    try {
      const value = performanceRef?.now?.();
      return typeof value === "number" && Number.isFinite(value) ? value : null;
    } catch (error) {
      return null;
    }
  };

  const getTimeSinceInitMs = () => {
    const currentMonotonic = monotonicNow();
    if (
      currentMonotonic !== null &&
      typeof state.initMonotonicAt === "number" &&
      Number.isFinite(state.initMonotonicAt)
    ) {
      return Math.max(0, Math.round(currentMonotonic - state.initMonotonicAt));
    }

    return state.initAt ? Math.max(0, Math.round(dateNow() - state.initAt)) : null;
  };

  const clearBootWatchdog = () => {
    if (state.bootTimerId !== null && windowRef?.clearTimeout) {
      windowRef.clearTimeout(state.bootTimerId);
      state.bootTimerId = null;
    }
  };

  const recoverFromChunkLoadFailure = ({ source = "", resourceType = null } = {}) => {
    addCrashBreadcrumb({
      category: "recovery",
      message: "chunk_load_recovery",
      data: {
        source,
        resourceType,
      },
    });

    if (!state.hasReactMounted) {
      state.preMountCrashCaptured = true;
      clearBootWatchdog();
    }

    if (shouldUseProductionBehavior()) {
      void attemptChunkLoadRecovery({
        enabled: true,
        windowRef,
        documentRef,
      });
    }
  };

  const recoverFromFirestoreClientFailure = ({ source = "" } = {}) => {
    if (state.firestoreClientRecoveryPending) {
      addCrashBreadcrumb({
        category: "recovery",
        message: "firestore_client_recovery_already_scheduled",
        data: {
          source,
        },
      });

      return { scheduled: true, reason: "already_scheduled" };
    }

    addCrashBreadcrumb({
      category: "recovery",
      message: "firestore_client_recovery",
      data: {
        source,
      },
    });

    if (shouldUseProductionBehavior()) {
      const recovery = attemptFirestoreClientRecovery({
        enabled: true,
        windowRef,
        documentRef,
      });

      if (recovery.scheduled) {
        state.firestoreClientRecoveryPending = true;
      }

      return recovery;
    }

    return { scheduled: false, reason: "disabled" };
  };

  const getNavigationType = () => {
    try {
      const [navigationEntry] =
        performanceRef?.getEntriesByType?.("navigation") || [];
      return truncateString(navigationEntry?.type, 40);
    } catch (error) {
      return null;
    }
  };

  const getScriptCount = () => {
    try {
      const scriptCount = documentRef?.querySelectorAll?.("script")?.length;
      return Number.isFinite(scriptCount) ? scriptCount : null;
    } catch (error) {
      return null;
    }
  };

  const buildBootRecoveryKey = () => {
    const origin = windowRef?.location?.origin || "http://localhost";
    const buildId = getCurrentMainAssetPath(documentRef, origin) || "unknown-build";
    const pathname = sanitizePath(windowRef?.location?.pathname || "/", origin) || "/";
    return `${buildId}|${pathname}`;
  };

  const reserveBootTimeoutRecovery = () => {
    if (!shouldUseProductionBehavior()) {
      return { attempted: false, reason: "disabled" };
    }

    const key = buildBootRecoveryKey();
    const previous = readJsonStorage(
      sessionStorageRef,
      BOOT_TIMEOUT_RECOVERY_STORAGE_KEY,
    );

    if (previous?.key === key) {
      return { attempted: false, reason: "already_attempted", key };
    }

    const recorded = writeJsonStorage(
      sessionStorageRef,
      BOOT_TIMEOUT_RECOVERY_STORAGE_KEY,
      {
        key,
        at: dateNow(),
      },
    );

    if (!recorded) {
      return { attempted: false, reason: "storage_unavailable", key };
    }

    return {
      attempted: true,
      reason: "boot_timeout",
      key,
      delayMs: DEFAULT_BOOT_TIMEOUT_RECOVERY_DELAY_MS,
    };
  };

  const buildBootContext = ({ recovery = null } = {}) => {
    const bootContext = {
      hasReactMounted: state.hasReactMounted,
      timeSinceInitMs: getTimeSinceInitMs(),
      readyState: truncateString(documentRef?.readyState, 40),
      visibilityState: truncateString(documentRef?.visibilityState, 40),
      isHidden: documentRef?.hidden === true,
      navigationType: getNavigationType(),
      mainScriptPath: truncateString(getCurrentMainAssetPath(documentRef), 200),
      scriptCount: getScriptCount(),
      webdriver: navigatorRef?.webdriver === true,
    };

    if (recovery) {
      bootContext.recovery = sanitizePayloadObject(
        {
          attempted: Boolean(recovery.attempted),
          reason: recovery.reason,
        },
        4,
        80,
      );
    }

    return bootContext;
  };

  const renderBootRecoveryFallback = () => {
    if (!shouldUseProductionBehavior() || !documentRef?.body) {
      return;
    }

    let overlay = documentRef.getElementById(EMERGENCY_OVERLAY_ID);

    if (!overlay) {
      overlay = documentRef.createElement("div");
      overlay.id = EMERGENCY_OVERLAY_ID;
      documentRef.body.appendChild(overlay);
    }

    overlay.setAttribute(
      "style",
      [
        "position:fixed",
        "inset:0",
        "z-index:2147483647",
        "display:flex",
        "align-items:center",
        "justify-content:center",
        "padding:24px",
        "background:#f8fafc",
        "font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      ].join(";"),
    );
    overlay.innerHTML = `
      <div style="max-width:520px;width:100%;border:1px solid #cbd5e1;border-radius:16px;padding:28px;background:#ffffff;box-shadow:0 20px 60px rgba(15,23,42,0.18);">
        <p style="margin:0 0 8px;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#64748b;">Linked Lives</p>
        <h1 style="margin:0;font-size:28px;line-height:1.1;color:#0f172a;">Refreshing this page.</h1>
        <p style="margin:16px 0 0;font-size:16px;line-height:1.5;color:#334155;">
          The app did not finish starting, so we're trying again with a fresh page load.
        </p>
      </div>
    `;

    state.overlayVisible = true;
  };

  const scheduleBootTimeoutRecoveryReload = (recovery = {}) => {
    if (!recovery.attempted || !windowRef?.location?.reload) {
      return;
    }

    const reload = () => windowRef.location.reload();
    const delayMs =
      typeof recovery.delayMs === "number" && recovery.delayMs >= 0
        ? recovery.delayMs
        : DEFAULT_BOOT_TIMEOUT_RECOVERY_DELAY_MS;

    if (delayMs > 0 && typeof windowRef.setTimeout === "function") {
      windowRef.setTimeout(reload, delayMs);
      return;
    }

    reload();
  };

  const hideEmergencyFallback = () => {
    if (!documentRef?.getElementById) {
      return;
    }

    const overlay = documentRef.getElementById(EMERGENCY_OVERLAY_ID);
    if (overlay?.remove) {
      overlay.remove();
    }
    state.overlayVisible = false;
  };

  const subscribeToClientErrorNotification = async ({
    issueId = null,
    groupId = null,
    incidentId = null,
    channel = "",
    target = "",
  } = {}) => {
    if (!fetchImpl) {
      throw new Error("fetch_unavailable");
    }

    const normalizedChannel = String(channel || "").trim().toLowerCase();
    const normalizedTarget =
      normalizedChannel === "email"
        ? normalizeSubscriptionEmail(target)
        : normalizeSubscriptionPhone(target);

    if ((!issueId && !groupId) || !normalizedTarget) {
      throw new Error("invalid_subscription_payload");
    }

    const response = await fetchImpl(state.config.subscribeUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        issueId,
        groupId,
        incidentId,
        channel: normalizedChannel,
        target: normalizedTarget,
      }),
    });

    if (!response.ok) {
      throw new Error(`client_error_subscribe_failed_${response.status}`);
    }

    return response.json().catch(() => null);
  };

  const renderEmergencyFallback = ({
    incidentId = null,
    issueId = null,
    groupId = null,
  } = {}) => {
    if (!shouldUseProductionBehavior() || !documentRef?.body) {
      return;
    }

    let overlay = documentRef.getElementById(EMERGENCY_OVERLAY_ID);

    if (!overlay) {
      overlay = documentRef.createElement("div");
      overlay.id = EMERGENCY_OVERLAY_ID;
      documentRef.body.appendChild(overlay);
    }

    const incidentMarkup = incidentId
      ? `<p style="margin:12px 0 0;font-size:13px;color:#475569;">Incident ID: <code style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">${escapeHtml(incidentId)}</code></p>`
      : "";
    const notificationMarkup = issueId || groupId
      ? `
        <div style="margin-top:20px;padding:16px;border:1px solid #e2e8f0;border-radius:16px;background:#f8fafc;">
          <p style="margin:0;font-size:14px;line-height:1.5;color:#334155;">
            We've notified our software team and we're actively working on a fix. Leave your email or mobile number and we'll let you know when it's fixed.
          </p>
          <input id="${EMERGENCY_OVERLAY_ID}__target" type="text" inputmode="email" placeholder="Email or mobile number" style="margin-top:12px;width:100%;box-sizing:border-box;border:1px solid #cbd5e1;border-radius:12px;padding:12px 14px;font-size:15px;color:#0f172a;" />
          <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:12px;">
            <button id="${EMERGENCY_OVERLAY_ID}__email" style="appearance:none;border:1px solid #cbd5e1;border-radius:12px;padding:10px 16px;background:#ffffff;color:#0f172a;font-size:14px;font-weight:600;cursor:pointer;">Email me</button>
            <button id="${EMERGENCY_OVERLAY_ID}__sms" style="appearance:none;border:1px solid #cbd5e1;border-radius:12px;padding:10px 16px;background:#ffffff;color:#0f172a;font-size:14px;font-weight:600;cursor:pointer;">Text me</button>
          </div>
          <p id="${EMERGENCY_OVERLAY_ID}__status" style="margin:10px 0 0;font-size:13px;color:#475569;"></p>
        </div>
      `
      : `
        <p style="margin:16px 0 0;font-size:14px;line-height:1.5;color:#475569;">
          We've notified our software team and we're actively working on a fix.
        </p>
      `;

    overlay.setAttribute(
      "style",
      [
        "position:fixed",
        "inset:0",
        "z-index:2147483647",
        "display:flex",
        "align-items:center",
        "justify-content:center",
        "padding:24px",
        "background:#f8fafc",
        "font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      ].join(";"),
    );
    overlay.innerHTML = `
      <div style="max-width:560px;width:100%;border:1px solid #cbd5e1;border-radius:16px;padding:28px;background:#ffffff;box-shadow:0 20px 60px rgba(15,23,42,0.18);">
        <p style="margin:0 0 8px;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#64748b;">Linked Lives</p>
        <h1 style="margin:0;font-size:28px;line-height:1.1;color:#0f172a;">Something went wrong loading this page.</h1>
        <p style="margin:16px 0 0;font-size:16px;line-height:1.5;color:#334155;">
          We recorded the problem. Please refresh the page. If it keeps happening, our software team has been notified and is actively working on a fix.
        </p>
        ${incidentMarkup}
        ${notificationMarkup}
        <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:24px;">
          <button id="${EMERGENCY_OVERLAY_ID}__reload" style="appearance:none;border:none;border-radius:12px;padding:12px 18px;background:#0f172a;color:#ffffff;font-size:15px;font-weight:600;cursor:pointer;">Reload page</button>
          <button id="${EMERGENCY_OVERLAY_ID}__home" style="appearance:none;border:1px solid #cbd5e1;border-radius:12px;padding:12px 18px;background:#ffffff;color:#0f172a;font-size:15px;font-weight:600;cursor:pointer;">Go to homepage</button>
        </div>
        <p style="margin:18px 0 0;font-size:14px;line-height:1.5;color:#475569;">
          Support: <a href="tel:${escapeHtml(state.config.supportPhone)}" style="color:#0f172a;">${escapeHtml(formatSupportPhone(state.config.supportPhone))}</a>
          &nbsp;•&nbsp;
          <a href="mailto:${escapeHtml(state.config.supportEmail)}" style="color:#0f172a;">${escapeHtml(state.config.supportEmail)}</a>
        </p>
      </div>
    `;

    const reloadButton = documentRef.getElementById(
      `${EMERGENCY_OVERLAY_ID}__reload`,
    );
    if (reloadButton) {
      reloadButton.onclick = () => windowRef?.location?.reload?.();
    }

    const homeButton = documentRef.getElementById(`${EMERGENCY_OVERLAY_ID}__home`);
    if (homeButton) {
      homeButton.onclick = () => windowRef?.location?.assign?.("/");
    }

    if (issueId || groupId) {
      const targetInput = documentRef.getElementById(
        `${EMERGENCY_OVERLAY_ID}__target`,
      );
      const statusNode = documentRef.getElementById(
        `${EMERGENCY_OVERLAY_ID}__status`,
      );
      const emailButton = documentRef.getElementById(
        `${EMERGENCY_OVERLAY_ID}__email`,
      );
      const smsButton = documentRef.getElementById(`${EMERGENCY_OVERLAY_ID}__sms`);

      const setStatus = (message = "", isError = false) => {
        if (!statusNode) {
          return;
        }

        statusNode.textContent = message;
        statusNode.style.color = isError ? "#b91c1c" : "#475569";
      };

      const setBusy = (busy) => {
        [targetInput, emailButton, smsButton].forEach((element) => {
          if (element) {
            element.disabled = Boolean(busy);
          }
        });
      };

      const submitSubscription = async (channel) => {
        setBusy(true);
        setStatus("");
        try {
          const response = await subscribeToClientErrorNotification({
            issueId,
            groupId,
            incidentId,
            channel,
            target: targetInput?.value || "",
          });
          setStatus(response?.message || "We'll let you know when it's fixed.");
        } catch (error) {
          setStatus("Enter a valid email or mobile number.", true);
        } finally {
          setBusy(false);
        }
      };

      if (emailButton) {
        emailButton.onclick = () => {
          void submitSubscription("email");
        };
      }

      if (smsButton) {
        smsButton.onclick = () => {
          void submitSubscription("sms");
        };
      }
    }

    state.overlayVisible = true;
  };

  const enqueuePendingReport = (payload) => {
    state.pendingReports.push(payload);
    if (state.pendingReports.length > state.config.maxPendingReports) {
      state.pendingReports = state.pendingReports.slice(
        -state.config.maxPendingReports,
      );
    }
  };

  const sendWithFetch = async (payload) => {
    if (!fetchImpl) {
      throw new Error("fetch_unavailable");
    }

    const response = await fetchImpl(state.config.reportUrl, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`client_crash_report_failed_${response.status}`);
    }

    return response.json().catch(() => null);
  };

  const sendWithBeacon = (payload) => {
    if (!navigatorRef?.sendBeacon || typeof Blob !== "function") {
      return false;
    }

    try {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });

      return navigatorRef.sendBeacon(state.config.reportUrl, blob);
    } catch (error) {
      return false;
    }
  };

  const sendApiDependencyHealthReport = (payload) => {
    if (!state.config.enabled) {
      return;
    }

    const reportBody = JSON.stringify(payload);

    if (navigatorRef?.sendBeacon && typeof Blob === "function") {
      try {
        const sent = navigatorRef.sendBeacon(
          state.config.reportUrl,
          new Blob([reportBody], { type: "application/json" }),
        );
        if (sent) return;
      } catch (error) {
        // Fall through to fetch.
      }
    }

    if (!fetchImpl) {
      return;
    }

    fetchImpl(state.config.reportUrl, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: reportBody,
    }).catch(() => {});
  };

  const flushPendingReports = async ({ preferBeacon = false } = {}) => {
    if (!state.pendingReports.length) {
      return;
    }

    const pending = [...state.pendingReports];
    state.pendingReports = [];

    for (const payload of pending) {
      try {
        if (preferBeacon && sendWithBeacon(payload)) {
          continue;
        }
        await sendWithFetch(payload);
      } catch (error) {
        enqueuePendingReport(payload);
      }
    }
  };

  const buildPayload = ({
    kind,
    name,
    message,
    stack,
    componentStack = null,
    source = "client",
    whiteScreenImpactHint = false,
    bootRecovery = null,
  }) => {
    const locationRef = windowRef?.location || { origin: "http://localhost" };
    const classification = classifyClientCrashKind({
      kind,
      name,
      message,
      stack,
      source,
    });
    const payload = {
      kind: classification,
      message:
        sanitizeFreeText(message, 240) ||
        (classification === "boot_timeout"
          ? "React app did not mount before the boot watchdog deadline."
          : "Unknown client crash"),
      name: truncateString(name, 80),
      stack: sanitizeFreeText(stack, 6_000),
      componentStack: sanitizeFreeText(componentStack, 3_000),
      buildId: truncateString(getCurrentMainAssetPath(documentRef), 200),
      pathname: sanitizePath(locationRef.pathname, locationRef.origin),
      referrerPath: getReferrerPath(documentRef, locationRef),
      breadcrumbs: state.breadcrumbs.slice(-MAX_BREADCRUMBS),
      boot: buildBootContext({ recovery: bootRecovery }),
      source: truncateString(source, 40),
      user: sanitizeUserContext(state.userContext),
      session: sanitizeSessionContext(state.sessionContext),
      device:
        sanitizePayloadObject(
          sanitizeSessionContext(state.sessionContext)?.systemSummary,
          10,
          80,
        ) ||
        buildFallbackDeviceSummary({ navigatorRef, windowRef }),
      whiteScreenImpactHint:
        Boolean(whiteScreenImpactHint) ||
        !state.hasReactMounted ||
        WHITE_SCREEN_KINDS.has(classification),
    };

    return fitPayloadToBudget(payload);
  };

  const transmitPayload = async (payload, { preferResponse = false } = {}) => {
    if (!state.config.enabled) {
      return null;
    }

    if (preferResponse) {
      try {
        return await sendWithFetch(payload);
      } catch (error) {
        if (sendWithBeacon(payload)) {
          return null;
        }
        enqueuePendingReport(payload);
        return null;
      }
    }

    if (sendWithBeacon(payload)) {
      return null;
    }

    try {
      return await sendWithFetch(payload);
    } catch (error) {
      enqueuePendingReport(payload);
      return null;
    }
  };

  const capturePayload = async (
    payload,
    { preferResponse = false, suppressEmergencyFallback = false } = {},
  ) => {
    pruneRecentFingerprints();

    const fingerprint = buildClientCrashFingerprint(payload);
    if (state.recentFingerprints.has(fingerprint)) {
      return null;
    }

    state.recentFingerprints.set(fingerprint, dateNow());

    if (
      payload.whiteScreenImpactHint &&
      !state.hasReactMounted &&
      shouldUseProductionBehavior() &&
      !suppressEmergencyFallback
    ) {
      if (WHITE_SCREEN_KINDS.has(payload.kind)) {
        state.preMountCrashCaptured = true;
        if (payload.kind !== "boot_timeout") {
          clearBootWatchdog();
        }
      }
      renderEmergencyFallback();
    }

    const response = await transmitPayload(payload, { preferResponse });

    if (
      response?.incidentId &&
      payload.whiteScreenImpactHint &&
      !state.hasReactMounted &&
      !suppressEmergencyFallback
    ) {
      renderEmergencyFallback({
        incidentId: response.incidentId,
        issueId: response.issueId || null,
        groupId: response.groupId || null,
      });
    }

    return response || null;
  };

  const scheduleBootWatchdog = () => {
    clearBootWatchdog();

    if (
      !shouldUseProductionBehavior() ||
      state.hasReactMounted ||
      state.preMountCrashCaptured ||
      documentRef?.visibilityState === "hidden" ||
      !windowRef?.setTimeout
    ) {
      return;
    }

    state.bootTimerId = windowRef.setTimeout(async () => {
      state.bootTimerId = null;

      if (state.hasReactMounted) {
        return;
      }

      if (state.preMountCrashCaptured) {
        return;
      }

      if (documentRef?.visibilityState === "hidden") {
        scheduleBootWatchdog();
        return;
      }

      addCrashBreadcrumb({
        category: "boot",
        message: "boot_timeout",
      });

      const recovery = reserveBootTimeoutRecovery();
      state.bootRecovery = recovery;

      const payload = buildPayload({
        kind: "boot_timeout",
        name: "BootTimeoutError",
        message: "React app did not mount before the boot watchdog deadline.",
        source: "boot_watchdog",
        whiteScreenImpactHint: true,
        bootRecovery: recovery,
      });

      if (recovery.attempted) {
        renderBootRecoveryFallback();
        void capturePayload(payload, { suppressEmergencyFallback: true });
        scheduleBootTimeoutRecoveryReload(recovery);
        return;
      }

      const response = await capturePayload(payload, { preferResponse: true });

      if (response?.incidentId) {
        renderEmergencyFallback({
          incidentId: response.incidentId,
          issueId: response.issueId || null,
          groupId: response.groupId || null,
        });
      }
    }, state.config.bootTimeoutMs);
  };

  const installGoogleMapsAuthFailureHandler = () => {
    if (!windowRef) {
      return;
    }

    const priorHandler =
      typeof windowRef.gm_authFailure === "function"
        ? windowRef.gm_authFailure
        : null;
    state.priorGoogleMapsAuthFailureHandler = priorHandler;

    windowRef.gm_authFailure = () => {
      if (priorHandler) {
        try {
          priorHandler();
        } catch (error) {
          addCrashBreadcrumb({
            category: "external",
            message: "google_maps_prior_auth_failure_handler_failed",
          });
        }
      }

      if (state.googleMapsAuthFailureCaptured) {
        return;
      }
      state.googleMapsAuthFailureCaptured = true;

      addCrashBreadcrumb({
        category: "dependency",
        message: "google_maps_auth_failure",
      });

      sendApiDependencyHealthReport({
        schemaVersion: "api_dependency_health/client/v1",
        eventName: "api_dependency_failure",
        service: "google-maps-browser",
        provider: "google_maps",
        operation: "maps_js_auth",
        workflow: "google_geo_browser_key",
        route: "maps_js",
        statusClass: "auth_failure",
        errorCode: "google_maps_auth_failure",
        errorType: "api_key_auth",
        message: GOOGLE_MAPS_AUTH_FAILURE_MESSAGE,
        pathname: sanitizePath(windowRef?.location?.pathname, windowRef?.location?.origin),
        windowMinutes: 15,
        minDegradedCount: 3,
        criticalCount: 8,
      });
    };
  };

  const handleWindowError = (event) => {
    const target = event?.target;

    if (target && target !== windowRef) {
      const resourceType = String(target.tagName || "resource").toLowerCase();
      const resourceUrl = sanitizeFreeText(target.src || target.href || "", 240);

      if (
        !isAppJavaScriptResource({
          resourceType,
          resourceUrl,
          locationRef: windowRef?.location,
        })
      ) {
        addCrashBreadcrumb({
          category: "resource",
          message: "resource_load_failed",
          data: {
            resourceType,
          },
        });
        return;
      }

      recoverFromChunkLoadFailure({
        source: "window_error",
        resourceType,
      });
      return;
    }

    const details = getErrorDetails(event?.error || event?.message);
    if (isOpaqueCrossOriginScriptError(event, details, windowRef?.location)) {
      addCrashBreadcrumb({
        category: "crash",
        message: "opaque_script_error_ignored",
      });
      return;
    }

    const browserNoiseReason = isLikelyBenignBrowserNoise({
      details,
      source: "window_error",
      hasReactMounted: state.hasReactMounted,
    });
    if (browserNoiseReason) {
      addCrashBreadcrumb({
        category: "external",
        message: browserNoiseReason,
      });
      return;
    }

    const classification = classifyClientCrashKind({
      name: details.name,
      message: details.message,
      stack: details.stack,
      source: "window_error",
      filename: event?.filename,
      locationRef: windowRef?.location,
      hasReactMounted: state.hasReactMounted,
    });

    if (isLikelyThirdPartyBrowserBridgeError(details)) {
      addCrashBreadcrumb({
        category: "external",
        message: "ignored_browser_bridge_error",
      });
      return;
    }

    addCrashBreadcrumb({
      category: "crash",
      message: classification,
    });

    if (classification === "chunk_load_failure") {
      recoverFromChunkLoadFailure({ source: "window_error" });
      return;
    }

    if (classification === "firestore_client_failure") {
      const recovery = recoverFromFirestoreClientFailure({
        source: "window_error",
      });

      if (recovery.scheduled) {
        return;
      }
    }

    const payload = buildPayload({
      kind: classification,
      ...details,
      source: "window_error",
      whiteScreenImpactHint:
        !state.hasReactMounted || classification === "chunk_load_failure",
    });

    void capturePayload(
      payload,
      { preferResponse: true },
    );
  };

  const handleUnhandledRejection = (event) => {
    const details = getErrorDetails(event?.reason);
    const classification = classifyClientCrashKind({
      name: details.name,
      message: details.message,
      stack: details.stack,
      source: "unhandledrejection",
    });

    if (isLikelyThirdPartyBrowserBridgeError(details)) {
      addCrashBreadcrumb({
        category: "external",
        message: "ignored_browser_bridge_error",
      });
      return;
    }

    const browserNoiseReason = isLikelyBenignBrowserNoise({
      details,
      source: "unhandledrejection",
      hasReactMounted: state.hasReactMounted,
    });
    if (browserNoiseReason) {
      addCrashBreadcrumb({
        category: "external",
        message: browserNoiseReason,
      });
      return;
    }

    addCrashBreadcrumb({
      category: "crash",
      message: classification,
    });

    if (classification === "chunk_load_failure") {
      recoverFromChunkLoadFailure({ source: "unhandledrejection" });
      return;
    }

    if (classification === "firestore_client_failure") {
      const recovery = recoverFromFirestoreClientFailure({
        source: "unhandledrejection",
      });

      if (recovery.scheduled) {
        return;
      }
    }

    const payload = buildPayload({
      kind: classification,
      ...details,
      source: "unhandledrejection",
      whiteScreenImpactHint:
        !state.hasReactMounted || classification === "chunk_load_failure",
    });

    void capturePayload(
      payload,
      { preferResponse: true },
    );
  };

  const handleVisibilityChange = () => {
    if (documentRef?.visibilityState === "hidden") {
      clearBootWatchdog();
      void flushPendingReports({ preferBeacon: true });
      return;
    }

    if (!state.hasReactMounted) {
      scheduleBootWatchdog();
    }
  };

  const handleOnline = () => {
    void flushPendingReports({ preferBeacon: false });
  };

  const init = (options = {}) => {
    if (!windowRef || !documentRef) {
      return;
    }

    state.config = {
      ...state.config,
      ...options,
    };

    if (state.initialized) {
      return;
    }

    state.initialized = true;
    state.initAt = dateNow();
    state.initMonotonicAt = monotonicNow();

    addCrashBreadcrumb({
      category: "app",
      message: "crash_reporter_initialized",
    });

    installGoogleMapsAuthFailureHandler();
    windowRef.addEventListener("error", handleWindowError, true);
    windowRef.addEventListener("unhandledrejection", handleUnhandledRejection);
    documentRef.addEventListener("visibilitychange", handleVisibilityChange);
    windowRef.addEventListener("online", handleOnline);

    scheduleBootWatchdog();
  };

  const setUserContext = (value = {}) => {
    state.userContext = sanitizeUserContext(value);
  };

  const setSessionContext = (value = {}) => {
    state.sessionContext = sanitizeSessionContext(value);
  };

  const addCrashBreadcrumb = ({ category = "app", message = "", data = null } = {}) => {
    const breadcrumb = sanitizeBreadcrumb({
      at: new Date(dateNow()).toISOString(),
      category,
      message,
      data,
    });

    if (!breadcrumb) {
      return;
    }

    state.breadcrumbs.push(breadcrumb);
    if (state.breadcrumbs.length > MAX_BREADCRUMBS) {
      state.breadcrumbs = state.breadcrumbs.slice(-MAX_BREADCRUMBS);
    }
  };

  const markReactMounted = () => {
    if (state.hasReactMounted) {
      return;
    }

    state.hasReactMounted = true;
    state.preMountCrashCaptured = false;
    state.bootRecovery = null;
    clearBootWatchdog();
    hideEmergencyFallback();
  };

  const captureBoundaryError = async (error, { componentStack = null } = {}) => {
    const details = getErrorDetails(error);
    const classification = classifyClientCrashKind({
      name: details.name,
      message: details.message,
      stack: details.stack,
      source: "react_boundary",
    });

    addCrashBreadcrumb({
      category: "crash",
      message: classification,
    });

    if (classification === "chunk_load_failure") {
      recoverFromChunkLoadFailure({ source: "react_boundary" });
      return null;
    }

    if (classification === "firestore_client_failure") {
      const recovery = recoverFromFirestoreClientFailure({
        source: "react_boundary",
      });

      if (recovery.scheduled) {
        return {
          recoveryScheduled: true,
          recoveryKind: "firestore_client_failure",
          recoveryReason: recovery.reason,
        };
      }
    }

    return capturePayload(
      buildPayload({
        kind: classification,
        ...details,
        componentStack,
        source: "react_boundary",
        whiteScreenImpactHint: true,
      }),
      { preferResponse: true },
    );
  };

  return {
    init,
    setUserContext,
    setSessionContext,
    addCrashBreadcrumb,
    markReactMounted,
    captureBoundaryError,
    subscribeToClientErrorNotification,
    flushPendingReports,
    __private__: {
      buildPayload,
      capturePayload,
      clearBootWatchdog,
      handleUnhandledRejection,
      handleVisibilityChange,
      handleWindowError,
      hideEmergencyFallback,
      isOpaqueCrossOriginScriptError,
      renderEmergencyFallback,
      reserveBootTimeoutRecovery,
      scheduleBootWatchdog,
      scheduleBootTimeoutRecoveryReload,
      state,
    },
  };
};

const clientCrashReporter = createClientCrashReporter();

export const initClientCrashReporter = (options) =>
  clientCrashReporter.init(options);

export const setUserContext = (value) => clientCrashReporter.setUserContext(value);

export const setSessionContext = (value) =>
  clientCrashReporter.setSessionContext(value);

export const addCrashBreadcrumb = (value) =>
  clientCrashReporter.addCrashBreadcrumb(value);

export const markReactMounted = () => clientCrashReporter.markReactMounted();

export const captureBoundaryError = (error, options) =>
  clientCrashReporter.captureBoundaryError(error, options);

export const subscribeToClientErrorNotification = (payload) =>
  clientCrashReporter.subscribeToClientErrorNotification(payload);

export { clientCrashReporter };
